import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Inject, Optional, ViewContainerRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { App } from '@df/core/app';
import { V12RetailFinance } from '@df/module-V12/v12-retail-finance.namespace';
import { Basket } from '@df/session/api/basket/basket';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { BodyClassService } from '@df/ui/body-class.service';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { IValidtionEvent } from '@icms/core/content/component/icms-content-component-validation.service';
import compact from 'lodash-es/compact';
import map from 'lodash-es/map';
import uniqBy from 'lodash-es/uniqBy';
import { Subject } from 'rxjs';
import { NModalLayoutRight } from '../../../ui/modal/modal-layout-right.component';
import { IPlaceholderData } from '../cms-text-processor/cms-text-processor.interfaces';
import { CmsTextRendererComponent } from '../cms-text-processor/cms-text-renderer.component';

export interface IV12RetailFinanceModalBlockData extends Catalog.IBlockData<IV12RetailFinanceModalBlockDataContent> { }

export interface IV12RetailFinanceModalBlockDataContent {
    title: string;
    description?: string;
    footer?: string;
}

interface IV12RetailFinanceInfoItem {
    title: string;
    value: number | string;
    isPrice?: boolean;
}

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            vfinance: V12RetailFinance.IPaymentMethodConfig;
        }
    }
}

export const V12_RETAIL_FINANCE_FROM_MODAL_ID = 'v12RetailFinanceDetailsModal';

@DfCmsModal({
    id: V12_RETAIL_FINANCE_FROM_MODAL_ID,
    name: 'V12 Retail Finance Details Modal',
    position: 'right',
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: NModalLayoutRight.getWidth(sizeName),
        modalClass: 'bg-col-w p-l-0 p-r-0 p-t-0'
    }),
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: ['S', 'M', 'L', 'X']
})
@ICmsComponent<V12RetailFinanceDetailsModalComponent>({
    id: 'v12_retail_finance_details_modal',
    block: {
        url: 'v12_retail_finance_details_modal'
    },
    name: 'V12 Retail Finance Details Modal',
    description: 'V12 Retail Finance Details Modal Content',
    fields: [
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Main title',
            default: 'V12 Retail Finance',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'description',
            type: NICmsContentField.EFieldType.text,
            title: 'Description'
        },
        <NICmsContentField.IField>{
            key: 'footer',
            type: NICmsContentField.EFieldType.text,
            title: 'Footer'
        }
    ],
    onDemandOnly: true
})
@Component({
    selector: 'v12-retail-finance-details-modal',
    templateUrl: './v12-retail-finance-details-modal.component.html',
    styleUrls: ['./v12-retail-finance-details-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        TranslationModule,
        FormsModule,
        GridModule,
        PriceComponent,
        CommonModule,
        ICmsClientModule,
        CmsTextRendererComponent,
    ]
})
export class V12RetailFinanceDetailsModalComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: IV12RetailFinanceModalBlockDataContent;

    info?: V12RetailFinance.IInfo;

    private _depositOptionValue?: number;

    private _packOptionId?: string;

    private readonly bodyClassName = 'v12-retail-finance-details-modal-open';

    constructor(
        private modalRef: ModalRef<V12RetailFinanceDetailsModalComponent>,
        private basket: Basket,
        public app: App,
        @Optional() @Inject(MODAL_DATA) public data: { amount: number },
        private bodyClassService: BodyClassService
    ) {
        super();
    }
    outletOptions?: NICmsContentComponent.IOutletOptions;
    iCmsValidation$?: Subject<void | IValidtionEvent>;
    node?: HTMLElement | ElementRef<HTMLElement>;
    viewContainerRef?: ViewContainerRef;
    variant?: string;
    _setICmsContent?: (content: NICmsContentComponent.IData) => void;
    _iCmsMeta?: NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>;

    override ngOnInit(): void {
        super.ngOnInit();
        this.setDefaultSelection();
        this.bodyClassService.add(this.bodyClassName, this);
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.bodyClassService.remove(this.bodyClassName, this);
    }

    setDefaultSelection(): void {
        if (this.depositOptions?.length > 0) {
            this.depositOptionValue = this.depositOptions[0].value;
        }

        if (this.packOptions?.length > 0) {
            const totalAmount = this.data.amount || this.basket?.totals?.subtotal;
            if (typeof totalAmount === 'undefined') return;
            let defaultOption = this.packOptions.find(packOption => packOption.months === 48);

            if (defaultOption) {
                this.packOptionId = defaultOption.id;
            }
        }
    }

    get depositOptionValue(): number | undefined {
        return this._depositOptionValue;
    }

    set depositOptionValue(value: number | undefined) {
        this._depositOptionValue = typeof value === 'string' ? parseFloat(value) : value;
        this.updateInfo();
        this.detectChanges();
    }

    get depositOptions(): V12RetailFinance.IDepositOption[] {
        if (this.app?.data?.vfinance?.v12percent) {
            return uniqBy(
                map(this.app.data.vfinance.v12percent, (value, key) => {
                    return {
                        value: parseFloat(key),
                        title: value
                    };
                }),
                i => i.value
            );
        }
        return [];
    }

    get depositOption(): V12RetailFinance.IDepositOption | undefined {
        if (typeof this.depositOptionValue !== 'undefined') {
            return this.depositOptions.find(i => i.value === this.depositOptionValue);
        }
        return undefined;
    }

    trackByDepositValue(index: number, depositOption: V12RetailFinance.IDepositOption): number {
        return depositOption.value;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Package Plan
    ////////
    ////////////////////////////////////////////

    get packOptionId(): string | undefined {
        return this._packOptionId;
    }

    set packOptionId(value: string | undefined) {
        this._packOptionId = value;
        this.updateInfo();
        this.detectChanges();
    }

    get packOptions(): V12RetailFinance.IPackOption[] {
        if (this.depositOption) {
            if (this.app?.data?.vfinance?.v12pack) {
                return uniqBy(
                    compact(
                        this.app?.data?.vfinance?.v12pack
                            .map(i => {
                                const v12package = i.package;
                                const packageParts = v12package?.split('/');
                                const totalAmount = this.data.amount || this.basket?.totals?.subtotal;
                                if (typeof totalAmount === 'undefined') return null;
                                if (
                                    packageParts &&
                                    v12package &&
                                    (i.min_finance_amount || 0) <= totalAmount &&
                                    (i.max_finance_amount || 0) >= totalAmount
                                ) {
                                    return {
                                        id: v12package,
                                        months: parseInt(packageParts[2], 10),
                                        interest: parseFloat(packageParts[3]),
                                        apr: parseFloat(packageParts[4]),
                                        title: packageParts[5],
                                        minFinanceAmount: i.min_finance_amount || 0,
                                        maxFinanceAmount: i.max_finance_amount || Infinity
                                    };
                                } else {
                                    return null;
                                }
                            })
                            .filter(option => option !== null)
                    ),
                    i => i?.id
                );
            }
            return [];
        }
        return [];
    }

    get packOption(): V12RetailFinance.IPackOption | undefined {
        if (this.packOptionId) {
            return this.packOptions.find(i => i.id === this.packOptionId);
        }
        return undefined;
    }

    trackByPackId(index: number, packOption: V12RetailFinance.IPackOption): string {
        return packOption.id;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Info
    ////////
    ////////////////////////////////////////////

    private updateInfo(): void {
        this.info = this.getInfo();
    }

    private getInfo(): V12RetailFinance.IInfo | undefined {
        const depositOption = this.depositOption;
        const packOption = this.packOption;

        if (depositOption && packOption) {
            const productName = packOption.title;
            const totalAmount = this.data.amount || this.basket?.totals?.subtotal;
            if (typeof totalAmount === 'undefined') return undefined;
            const depositAmount = (totalAmount / 100) * depositOption.value;
            const loanAmount = (totalAmount * 100 - depositAmount * 100) / 100;
            const interestRate = packOption.apr;
            const monthlyPayment = Math.round(loanAmount * packOption.interest * 100) / 100;
            const terms = packOption.months;
            const totalAmountPayable = interestRate === 0 ? loanAmount : monthlyPayment * terms;
            const creditAmount = Math.abs(totalAmountPayable * 100 - loanAmount * 100) / 100;

            return {
                productName,
                totalAmount,
                depositAmount,
                loanAmount,
                interestRate,
                monthlyPayment,
                terms,
                creditAmount,
                totalAmountPayable
            };
        }
        return undefined;
    }

/**
 * Calculate Annual Rate of Interest (Fixed)
 */
    get annualRateOfInterest(): number {
        if (!this.info) return 0;

        const { loanAmount, creditAmount, terms } = this.info;

        if (loanAmount <= 0 || terms <= 0) return 0;

        const interest = creditAmount;
        const annualRate = (((interest / loanAmount) * 100) / (terms / 12));

        return annualRate;
    }

    get infoItems(): IV12RetailFinanceInfoItem[] {
        if (this.info) {
            const {
                productName,
                totalAmount,
                depositAmount,
                loanAmount,
                interestRate,
                monthlyPayment,
                terms,
                creditAmount,
                totalAmountPayable
            } = this.info;
            return [
                { title: 'Product Name', value: productName },
                { title: 'Cash Price', value: totalAmount, isPrice: true },
                { title: 'Amount of Deposit', value: depositAmount, isPrice: true },
                { title: 'Total Amount of Credit', value: loanAmount, isPrice: true },
                { title: 'Representative APR %', value: `${interestRate}%` },
                { title: 'Annual Rate of Interest (Fixed)', value: `${this.annualRateOfInterest.toFixed(2)}%` },
                { title: 'Monthly Repayment', value: monthlyPayment, isPrice: true },
                { title: 'Loan Term', value: terms },
                { title: 'Total Cost of Credit', value: creditAmount, isPrice: true },
                { title: 'Total Amount Payable', value: totalAmountPayable, isPrice: true }
            ];
        }
        return [];
    }

    trackByInfoItemTitleValue(index: number, infoItem: IV12RetailFinanceInfoItem): string {
        return `${infoItem.title}_${infoItem.value}`;
    }

    close(): void {
        this.modalRef.close();
    }
    asNumber(value: string | number): number {
        return typeof value === 'string' ? parseFloat(value) : value;
    }

    selectPackOption(packOptionId: string): void {
        this.packOptionId = packOptionId;
    }

    getMonthlyPriceForPackOption(packOption: V12RetailFinance.IPackOption): number {
        const totalAmount = this.data.amount || this.basket?.totals?.subtotal || 0;
        const depositAmount = (totalAmount / 100) * (this.depositOption?.value || 0);
        const loanAmount = (totalAmount * 100 - depositAmount * 100) / 100;
        return Math.round(loanAmount * packOption.interest * 100) / 100;
    }

    /**
     * Get placeholder data for CMS text processing
     * Used to replace dynamic placeholders in CMS content
     */
    get placeholderData(): IPlaceholderData {
        const totalAmount = this.data.amount || this.basket?.totals?.subtotal || 0;
        const depositPercentage = this.depositOption?.value || 0;
        const depositAmount = (totalAmount / 100) * depositPercentage;
        const loanAmount = (totalAmount * 100 - depositAmount * 100) / 100;
        const monthlyPayment = this.info?.monthlyPayment || 0;
        const interestRate = this.info?.interestRate || 0;
        const terms = this.info?.terms || 0;

        return {
            productPrice: totalAmount,
            depositPercentage: depositPercentage,
            depositValue: depositAmount,
            loanAmount: loanAmount,
            monthlyPayment: monthlyPayment,
            totalAmount: totalAmount,
            interestRate: interestRate,
            terms: terms
        };
    }
}
